import { Modal } from "newui/components/ui/Modal";
import { scopeKey, scopeTypeStyle } from "newui/features/RolesPermissions";
import type { PendingDowngrade } from "./useScopeSelection";

export interface DowngradeConfirmModalProps {
	pendingDowngrade: PendingDowngrade | null;
	onConfirm: () => void;
	onCancel: () => void;
}

// Rule 2 — IN-SESSION only: the admin picked lower-level scopes this session,
// then picked a higher-order one that now covers them. Client-side, never
// calls preview(). Distinct from D8-T3's server-derived R1 auto-cleanup,
// which is about EXISTING assignments found at preview time — see the
// boundary note in useScopeSelection.ts and AccessConfirmModal.tsx.
export const DowngradeConfirmModal = ({
	pendingDowngrade,
	onConfirm,
	onCancel,
}: DowngradeConfirmModalProps) => {
	return (
		<Modal
			isOpen={pendingDowngrade !== null}
			modalType="alert"
			iconType="warning"
			title="Remove more specific selections?"
			closeOnEsc
			primaryButton={{
				text: "Confirm",
				btntype: "primary",
				onClick: onConfirm,
			}}
			secondaryButton={{
				text: "Cancel",
				onClick: onCancel,
			}}
			onClose={onCancel}
		>
			<div className="space-y-3 text-sm text-gray-700" data-testid="downgrade-confirm-modal">
				{pendingDowngrade && (
					<>
						{pendingDowngrade.covered.length > 0 && (
							<>
								<p>
									<span className="font-semibold">{pendingDowngrade.option.label}</span> already
									covers the following selections. Picking it will remove them, since access to
									everything beneath it is already included:
								</p>
								<ul
									className="list-inside list-disc space-y-1 rounded-md bg-gray-50 px-3 py-2"
									data-testid="downgrade-covered-list"
								>
									{pendingDowngrade.covered.map((option) => (
										<li key={scopeKey(option.scope_type, option.scope_id)} className="font-medium">
											{option.label}
											<span
												className={`ml-2 inline-flex rounded-full border px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${scopeTypeStyle(option.scope_type).chip}`}
											>
												{scopeTypeStyle(option.scope_type).label}
											</span>
										</li>
									))}
								</ul>
							</>
						)}
						{/*
							F4 — rows whose real parent is withheld (bidder/hybrid company
							invited RFx tree, PRO-719) can't be verified as covered OR
							uncovered by `pendingDowngrade.option`. Rather than silently
							dropping rule 2 for them, or guessing, they get their own
							explicit, visually distinct "can't verify" section — and, per
							useScopeSelection's confirmDowngrade, they are NEVER auto-removed.
						*/}
						{pendingDowngrade.uncertain.length > 0 && (
							<>
								<p>
									<span className="font-semibold">{pendingDowngrade.option.label}</span> may also
									cover the following selections, but their exact location isn&apos;t shown to
									you, so this can&apos;t be confirmed automatically. They will{" "}
									<span className="font-semibold">not</span> be removed — check them yourself
									before continuing:
								</p>
								<ul
									className="list-inside list-disc space-y-1 rounded-md border border-amber-300 bg-amber-50 px-3 py-2"
									data-testid="downgrade-uncertain-list"
								>
									{pendingDowngrade.uncertain.map((option) => (
										<li key={scopeKey(option.scope_type, option.scope_id)} className="font-medium">
											{option.label}
											<span
												className={`ml-2 inline-flex rounded-full border px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${scopeTypeStyle(option.scope_type).chip}`}
											>
												{scopeTypeStyle(option.scope_type).label}
											</span>
										</li>
									))}
								</ul>
							</>
						)}
					</>
				)}
			</div>
		</Modal>
	);
};
